import React from 'react';
import ReactEChartsCore from 'echarts-for-react';

interface CIItem {
  label: string;
  estimate: number;
  lower: number;
  upper: number;
  significant?: boolean;
}

interface ConfidenceIntervalChartProps {
  title: string;
  items: CIItem[];
  referenceLine?: number;
  referenceLabel?: string;
}

const ConfidenceIntervalChart: React.FC<ConfidenceIntervalChartProps> = ({
  title,
  items,
  referenceLine,
  referenceLabel,
}) => {
  if (!items || items.length === 0) return null;

  const labels = items.map(d => d.label);
  const estimates = items.map(d => d.estimate);
  const lowers = items.map(d => d.lower);
  const uppers = items.map(d => d.upper);

  // Build custom series for error bars
  const errorBarData = items.map((d, i) => [i, d.lower, d.upper]);

  const option: any = {
    title: { text: title, left: 'center', textStyle: { fontSize: 15, color: '#061b31' } },
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        if (params.seriesName === '置信区间') return '';
        const idx = params.dataIndex;
        const item = items[idx];
        return `<b>${item.label}</b><br/>估计值: ${item.estimate.toFixed(4)}<br/>95% CI: [${item.lower.toFixed(4)}, ${item.upper.toFixed(4)}]`;
      },
    },
    grid: { left: '20%', right: '10%', top: '15%', bottom: '10%' },
    xAxis: {
      type: 'value',
      name: '值',
      axisLine: { lineStyle: { color: '#ccc' } },
    },
    yAxis: {
      type: 'category',
      data: labels,
      axisLine: { lineStyle: { color: '#ccc' } },
      axisTick: { show: false },
    },
    series: [
      {
        name: '估计值',
        type: 'scatter',
        data: estimates.map((v, i) => [v, i]),
        symbolSize: 10,
        itemStyle: {
          color: (params: any) => {
            const item = items[params.dataIndex];
            return item.significant === false ? '#999' : '#533afd';
          },
        },
        z: 3,
      },
      // CI whiskers using markLine on scatter (more compatible than custom series)
      ...items.map((item, i) => ({
        name: `CI-${i}`,
        type: 'line' as const,
        data: [[item.lower, i], [item.upper, i]],
        symbol: ['|', '|'],
        symbolSize: [2, 12],
        lineStyle: { color: '#533afd', width: 2 },
        z: 2,
        tooltip: { show: false },
      })),
    ],
  };

  // Reference line (e.g., null hypothesis value)
  if (referenceLine !== undefined) {
    option.series.push({
      name: referenceLabel || '参考线',
      type: 'line',
      markLine: {
        silent: true,
        symbol: 'none',
        lineStyle: { color: '#df1b41', type: 'dashed', width: 1.5 },
        data: [{ xAxis: referenceLine }],
        label: { formatter: referenceLabel || `${referenceLine}`, position: 'end', fontSize: 11 },
      },
      data: [],
    });
  }

  return (
    <div className="card-trendata" style={{ padding: 20, marginBottom: 16 }}>
      <ReactEChartsCore
        option={option}
        style={{ height: Math.max(200, items.length * 40 + 80), width: '100%' }}
        notMerge={true}
      />
    </div>
  );
};

export default ConfidenceIntervalChart;
